
import React from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { ColumnBlockConfiguration } from '@/types/block-configurations';
import { DragGhostPlaceholder } from '../DragGhostPlaceholder';
import { useDragState } from '../DndProvider';

export function ColumnBlock({
  block,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  children
}: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as ColumnBlockConfiguration;
  const { dropTarget, activeData } = useDragState();

  // Check if children exist and count them
  const hasChildren = children && React.Children.count(children) > 0;
  const childrenArray = hasChildren ? React.Children.toArray(children) : [];

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onHover(block.id);
      }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-column cursor-pointer rounded transition-all min-w-[120px]
        ${config.width && config.width !== 'auto' ? '' : 'flex-1'}
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        // Mirrors mj-column: explicit width (e.g. "50%") wins, otherwise equal share
        flexBasis: config.width && config.width !== 'auto' ? config.width : undefined,
        flexGrow: config.width && config.width !== 'auto' ? 0 : undefined,
        backgroundColor: config.background?.color || 'transparent',
        backgroundImage: config.background?.imageUrl ? `url(${config.background.imageUrl})` : undefined,
        backgroundSize: config.background?.imageSize || 'cover',
        backgroundRepeat: config.background?.imageRepeat || 'no-repeat',
        paddingTop: `${config.padding?.top ?? 0}px`,
        paddingRight: `${config.padding?.right ?? 0}px`,
        paddingBottom: `${config.padding?.bottom ?? 0}px`,
        paddingLeft: `${config.padding?.left ?? 0}px`,
        borderRadius: `${config.border?.radius || 0}px`,
        borderWidth: config.border?.width ? `${config.border.width}px` : '0',
        borderStyle: config.border?.style || 'solid',
        borderColor: config.border?.color || '#000000',
        verticalAlign: config.verticalAlign || 'top',
      }}
    >
      {hasChildren ? (
        // Render children with ghost placeholder at drop target index
        <div className="lw-block-column-children space-y-2">
          {childrenArray.map((child, idx) => (
            <React.Fragment key={`child-${idx}`}>
              {dropTarget?.parentId === block.id && dropTarget.index === idx && (
                <DragGhostPlaceholder />
              )}
              {child}
            </React.Fragment>
          ))}
          {dropTarget?.parentId === block.id && dropTarget.index === childrenArray.length && (
            <DragGhostPlaceholder />
          )}
        </div>
      ) : (
        // Empty state — visible placeholder
        <div className={`rounded-lg border-2 border-dashed flex items-center justify-center min-h-16 ${
          activeData ? 'border-primary/40 bg-primary/5' : 'border-border/60'
        }`}>
          {dropTarget?.parentId === block.id ? (
            <DragGhostPlaceholder />
          ) : (
            <p className={`text-sm font-medium select-none ${
              activeData ? 'text-primary/60' : 'text-muted-foreground/50'
            }`}>
              Drop content here
            </p>
          )}
        </div>
      )}
    </div>
  );
}
